import { Injectable, inject, DestroyRef, Injector, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PreloadConfigService } from '@common/config/preload-config.service';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UpdateServiceWorkerService {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly shouldRefreshConfig = (environment as { isKiosk?: boolean }).isKiosk === true;

  private isPolling = false;

  private get isSwEnabled(): boolean {
    return this.swUpdate?.isEnabled === true;
  }

  constructor() {
    if (this.isSwEnabled) {
      console.log('[SW] Service Worker habilitado. Iniciando gestión de actualizaciones.');
      this.listenForUpdates();
    }

    if (this.shouldRefreshConfig) {
      console.log('[CONFIG] Refresco periódico de config.json habilitado para kiosko.');
    }

    this.setupUpdatePolling();
  }

  private readonly ngZone = inject(NgZone);

  /**
   * Configura el sondeo periódico para buscar actualizaciones
   * Se ejecuta cada 5 minutos
   */
  private setupUpdatePolling(): void {
    // Verificamos cada 5 minutos
    this.ngZone.runOutsideAngular(() => {
      interval(5 * 60 * 1000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async () => {
        if (this.isPolling) return;
        this.isPolling = true;

        try {
          const swUpdate = this.swUpdate;
          if (swUpdate?.isEnabled) {
            // Solo logueamos si hay actualización o error para no ensuciar la consola
            const updateFound = await swUpdate.checkForUpdate();
            if (updateFound) {
              console.log('[SW] ¡Actualización encontrada en el servidor!');
            }
          }

          if (this.shouldRefreshConfig) {
            const configChanged = await this.injector.get(PreloadConfigService).refreshConfig();
            if (configChanged) {
              console.log('[CONFIG] Se detectaron cambios en config.json y se aplicaron en memoria.');
            }
          }
        } catch (error) {
          console.error('[UPDATE LOOP] Error durante la comprobación periódica:', error);
        } finally {
          this.isPolling = false;
        }
      });
    });
  }

  /**
   * Escucha actualizaciones y recarga automáticamente cuando están listas
   */
  private listenForUpdates(): void {
    const swUpdate = this.swUpdate;
    if (!swUpdate) return;

    swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((evt) => {
        console.log('[SW] Nueva versión lista para activar. Recargando...', evt.latestVersion);
        swUpdate.activateUpdate().then(() => this.reloadPage());
      });
  }

  protected reloadPage(): void {
    document.location.reload();
  }
}
